import React, { useState, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import {
  getPublicStrings,
  getLiveGrips,
  createWhatsappStringBooking,
  createWhatsappGripBooking,
  getActiveDiscountApi,
  getExpressSettingsApi,
} from "../../api";
import { getDubaiDate } from "../../utils/date";
import { normalizeLinkPhone, normalizeTypedPhone } from "../../utils/phone";
import CustomDropdown from "./CustomDropdown";
import styles from "./WhatsappBookingForm.module.css";

const WhatsappBookingForm = () => {
  const location = useLocation();
  const { executeRecaptcha } = useGoogleReCaptcha();

  // Parse phone and name from query parameters
  const getQueryParams = () => {
    const searchParams = new URLSearchParams(location.search);
    return {
      phone: searchParams.get("phone") || "",
      name: searchParams.get("name") || "",
    };
  };

  const { phone: queryPhone, name: queryName } = getQueryParams();

  // Form State
  const [customerName, setCustomerName] = useState(queryName);
  const [customerEmail, setCustomerEmail] = useState("");
  const [customerPhone, setCustomerPhone] = useState(queryPhone);
  const [phoneTouched, setPhoneTouched] = useState(false);
  // A valid number from the WhatsApp link is used as is; otherwise the customer types one
  const linkPhone = normalizeLinkPhone(queryPhone);
  const typedPhone = normalizeTypedPhone(customerPhone);
  // Numbers we can't recognise are still saved as typed, so the shop can call them
  const bookingPhone = linkPhone || typedPhone || customerPhone.trim();
  const [serviceKind, setServiceKind] = useState("string"); // 'string' | 'grip'
  const [racketName, setRacketName] = useState("");
  const [serviceSpeed, setServiceSpeed] = useState("Regular"); // 'Regular' | 'Express'
  const [pickupOption, setPickupOption] = useState("in-store"); // 'in-store' | 'pickup/delivery'
  const [instoreLocation, setInstoreLocation] = useState("Jumeirah"); // 'Jumeirah' | 'Dubai Hills'
  const [deliveryAddress, setDeliveryAddress] = useState("");
  const [, setPaymentOption] = useState("online"); // 'online' | 'in-store'

  // String Selection State
  const [strings, setStrings] = useState([]);
  const [selectedStringId, setSelectedStringId] = useState("");
  const [selectedString, setSelectedString] = useState(null);
  const [tensionOption, setTensionOption] = useState("standard"); // 'standard' | 'custom' | 'support'
  const [tensionOptionMain, setTensionOptionMain] = useState("standard");
  const [tensionOptionCross, setTensionOptionCross] = useState("standard");
  const [tensionMain, setTensionMain] = useState("");
  const [tensionCross, setTensionCross] = useState("");
  const [tensionUnit, setTensionUnit] = useState("lbs");
  const [prestretch, setPrestretch] = useState(false);
  const [prestretchPercent, setPrestretchPercent] = useState("10");

  const [isHybrid, setIsHybrid] = useState(false);
  const [selectedStringIdCross, setSelectedStringIdCross] = useState("");
  const [selectedStringCross, setSelectedStringCross] = useState(null);
  const [prestretchCross, setPrestretchCross] = useState(false);
  const [prestretchPercentCross, setPrestretchPercentCross] = useState("10");
  const [showHybridInfo, setShowHybridInfo] = useState(false);
  const hybridInfoRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (hybridInfoRef.current && !hybridInfoRef.current.contains(event.target)) {
        setShowHybridInfo(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Grip Selection State
  const [grips, setGrips] = useState([]);
  const [selectedGripSku, setSelectedGripSku] = useState("");

  // Status & UI States
  const [loadingInventory, setLoadingInventory] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [activeDiscount, setActiveDiscount] = useState(null);
  const [expressSettings, setExpressSettings] = useState({
    is_express_enabled: true,
    marina_express_enabled: false,
    jumeirah_express_enabled: true,
    dubaihills_express_enabled: false,
    whatsapp_express_enabled: true,
    cutoff_hour: 17,
  });

  // Fetch Inventory (Strings, Grips & Active Discount & Express Settings)
  useEffect(() => {
    const fetchInventory = async () => {
      setLoadingInventory(true);
      try {
        const [stringsRes, gripsData, discountRes, settingsRes] = await Promise.all([
          getPublicStrings(),
          getLiveGrips(),
          getActiveDiscountApi().catch((err) => {
            console.error("Error loading active discount:", err);
            return { data: { active: false } };
          }),
          getExpressSettingsApi().catch((err) => {
            console.error("Error loading express settings:", err);
            return {
              data: {
                is_express_enabled: true,
                marina_express_enabled: false,
                jumeirah_express_enabled: true,
                dubaihills_express_enabled: true,
                whatsapp_express_enabled: true,
                cutoff_hour: 17,
              },
            };
          }),
        ]);

        if (settingsRes?.data) {
          setExpressSettings(settingsRes.data);
        }
        
        // Filter out duplicate strings by string_id
        const uniqueStrings = [];
        const stringIds = new Set();
        
        // Add "Bring Your Own String" option first
        uniqueStrings.push({
          string_id: "byos",
          name: "Bring Your Own String",
          brand: "Custom",
          basePrice: 75.0,
          availableInAnyStore: true,
          jumeriah_stock: 99,
          dubaihills_stock: 99,
        });
        stringIds.add("byos");

        if (stringsRes && Array.isArray(stringsRes.data)) {
          for (const item of stringsRes.data) {
            if (item && item.string_id && !stringIds.has(item.string_id)) {
              const stock = Number(item.jumeriah_stock || 0) + Number(item.dubaihills_stock || 0);
              if (stock > 0) {
                stringIds.add(item.string_id);
                uniqueStrings.push(item);
              }
            }
          }
        }
        setStrings(uniqueStrings);

        // Parse and normalize grips exactly like the desktop GripBookingForm.js
        let items = gripsData;
        items = Array.isArray(items)
          ? items
          : Array.isArray(items?.data)
          ? items.data
          : [];

        const normalizeType = (t) => {
          if (!t) return "Regular";
          const s = String(t).toLowerCase();
          return s === "express" ? "Express" : "Regular";
        };

        const normalizedGrips = items.map((x) => ({
          ...x,
          type: normalizeType(x.type ?? x.service_type),
          basePrice: Number(x.basePrice ?? x.cost_per_unit ?? 20),
          stockStatus: x.stockStatus ?? x.stock_status ?? "available",
          qty: x.qty ?? x.remaining_quantity ?? 999,
          cost: Number(x.cost ?? x.bc_price_inc_tax ?? 0),
          sku: x.sku ?? x.product_code ?? null,
        }));

        // Filter out duplicate grips and out-of-stock grips
        const uniqueGrips = [];
        const gripKeys = new Set();
        
        for (const item of normalizedGrips) {
          const keyVal = item.sku || item.id || item.variant_id;
          if (keyVal && !gripKeys.has(keyVal)) {
            const isOutOfStock =
              item.stockStatus === "not_in_stock" ||
              Number(item.qty ?? 0) <= 0;
            if (!isOutOfStock) {
              gripKeys.add(keyVal);
              uniqueGrips.push(item);
            }
          }
        }
        setGrips(uniqueGrips);

        if (discountRes && discountRes.data && discountRes.data.active) {
          setActiveDiscount(discountRes.data.discount);
        }
      } catch (err) {
        console.error("Error loading inventory details:", err);
        setErrorMessage("Failed to load strings/grips inventory. Please refresh the page.");
      } finally {
        setLoadingInventory(false);
      }
    };
    fetchInventory();
  }, []);

  // Stock for the chosen store; Marina and pickup/delivery can use any store
  const getStockAtLocation = (item) => {
    if (!item) return 0;
    const jumeirah = Number(item.jumeriah_stock || 0);
    const dubaiHills = Number(item.dubaihills_stock || 0);
    if (pickupOption === "in-store" && instoreLocation === "Jumeirah") return jumeirah;
    if (pickupOption === "in-store" && instoreLocation === "Dubai Hills") return dubaiHills;
    return jumeirah + dubaiHills;
  };
  const storeLabel = `${instoreLocation} Store`;
  // Strings in stock at the chosen store first; the rest are shown disabled
  const sortedStrings = strings
    .slice()
    .sort((a, b) => (getStockAtLocation(b) > 0) - (getStockAtLocation(a) > 0));
  const getOtherStore = (item) => {
    if (pickupOption !== "in-store") return null;
    if (instoreLocation === "Jumeirah" && Number(item.dubaihills_stock || 0) > 0) return "Dubai Hills Store";
    if (instoreLocation === "Dubai Hills" && Number(item.jumeriah_stock || 0) > 0) return "Jumeirah Store";
    return null;
  };
  const renderStockNote = (item) => {
    if (getStockAtLocation(item) > 0) return null;
    const otherStore = getOtherStore(item);
    return (
      <span style={{ fontSize: "0.78rem", color: "#c2410c", fontWeight: "normal" }}>
        Not in stock at {storeLabel}{otherStore ? ` · available at ${otherStore}` : ""}
      </span>
    );
  };
  const [stringNotice, setStringNotice] = useState("");

  // Clear selected strings that aren't in stock at the newly chosen store
  useEffect(() => {
    if (strings.length === 0) return;
    const findString = (id) => strings.find((s) => String(s.string_id) === String(id));
    const cleared = [];
    if (selectedStringId && getStockAtLocation(findString(selectedStringId)) <= 0) {
      cleared.push(findString(selectedStringId)?.name);
      setSelectedStringId("");
    }
    if (selectedStringIdCross && getStockAtLocation(findString(selectedStringIdCross)) <= 0) {
      cleared.push(findString(selectedStringIdCross)?.name);
      setSelectedStringIdCross("");
    }
    if (cleared.length > 0) {
      const names = [...new Set(cleared.filter(Boolean))].join(" and ");
      setStringNotice(`${names || "Your selected string"} ${cleared.length > 1 ? "aren't" : "isn't"} in stock at ${storeLabel}. Please choose another string.`);
    }
  }, [pickupOption, instoreLocation, strings]); // eslint-disable-line react-hooks/exhaustive-deps

  // Update selected items based on dropdown selections
  useEffect(() => {
    if (selectedStringId) {
      const found = strings.find((s) => String(s.string_id) === String(selectedStringId));
      setSelectedString(found || null);
    } else {
      setSelectedString(null);
    }
  }, [selectedStringId, strings]);

  useEffect(() => {
    if (selectedStringIdCross) {
      const found = strings.find((s) => String(s.string_id) === String(selectedStringIdCross));
      setSelectedStringCross(found || null);
    } else {
      setSelectedStringCross(null);
    }
  }, [selectedStringIdCross, strings]);

  // Express availability check based on Cutoff Hour (Asia/Dubai timezone), Marina Store, and Global/WhatsApp toggles
  const cutoffHour = expressSettings?.cutoff_hour ?? 17;
  const { hour: currentDubaiHour } = getDubaiDate();
  const isAfterCutoff = currentDubaiHour >= cutoffHour;
  const isMarinaSelected = pickupOption === "in-store" && instoreLocation === "Marina";
  const isMarinaExpressBlocked = isMarinaSelected && !expressSettings?.marina_express_enabled;
  const isDubaiHillsSelected = pickupOption === "in-store" && instoreLocation === "Dubai Hills";
  const isDubaiHillsExpressBlocked = isDubaiHillsSelected && !expressSettings?.dubaihills_express_enabled;
  const isGlobalExpressBlocked = !expressSettings?.is_express_enabled || !expressSettings?.whatsapp_express_enabled;
  const isExpressAvailable = !isAfterCutoff && !isMarinaExpressBlocked && !isDubaiHillsExpressBlocked && !isGlobalExpressBlocked;

  let expressDisabledReason = "";
  if (isGlobalExpressBlocked) {
    expressDisabledReason = "Express Speed is currently unavailable";
  } else if (isMarinaExpressBlocked) {
    expressDisabledReason = "Express Speed is not available at Marina Store";
  } else if (isDubaiHillsExpressBlocked) {
    expressDisabledReason = "Express Speed is not available at Dubai Hills Store";
  } else if (isAfterCutoff) {
    const formattedHour = cutoffHour > 12 ? `${cutoffHour - 12}:00 PM` : `${cutoffHour}:00 AM`;
    expressDisabledReason = `Express Speed is not available after ${formattedHour} for same-day booking`;
  }

  // Automatically reset to Regular if Express becomes unavailable
  useEffect(() => {
    if (!isExpressAvailable && serviceSpeed === "Express") {
      setServiceSpeed("Regular");
    }
  }, [isExpressAvailable, serviceSpeed]);

  // Pricing helpers for calculations
  const isExpress = serviceSpeed === "Express";
  const pickupFee = isExpress
    ? ((pickupOption === "pickup" || pickupOption === "delivery") ? 50 : (pickupOption === "pickup/delivery" ? 100 : 0))
    : ((pickupOption === "pickup" || pickupOption === "delivery") ? 30 : (pickupOption === "pickup/delivery" ? 60 : 0));

  const isByosString = (str) => {
    if (!str) return true;
    if (str.string_id === "byos" || str.string_id === 0 || str.string_id === "0") return true;
    const name = (str.name || str.string_name || "").toLowerCase().trim();
    return name.includes("bring own") || name.includes("byos");
  };

  const findString = (id) => (id ? strings.find((s) => String(s.string_id) === String(id)) || null : null);
  const findGrip = (sku) => (sku ? grips.find((g) => g.sku === sku) || null : null);

  // ===== Rackets: finished rackets are kept in savedRackets; the editor holds the one being filled in =====
  const MAX_RACKETS = 5;
  const [savedRackets, setSavedRackets] = useState([]);
  // The racket form can be closed once at least one racket is saved
  const [isEditorOpen, setIsEditorOpen] = useState(true);
  const editorRef = useRef(null);
  const errorRef = useRef(null);

  const getEditorRacket = () => ({
    racketName,
    isHybrid,
    selectedStringId,
    selectedStringIdCross,
    tensionOption,
    tensionOptionMain,
    tensionOptionCross,
    tensionMain,
    tensionCross,
    tensionUnit,
    prestretch,
    prestretchPercent,
    prestretchCross,
    prestretchPercentCross,
    selectedGripSku,
  });

  const loadEditorRacket = (r) => {
    setRacketName(r.racketName);
    setIsHybrid(r.isHybrid);
    setSelectedStringId(r.selectedStringId);
    setSelectedStringIdCross(r.selectedStringIdCross);
    setTensionOption(r.tensionOption);
    setTensionOptionMain(r.tensionOptionMain);
    setTensionOptionCross(r.tensionOptionCross);
    setTensionMain(r.tensionMain);
    setTensionCross(r.tensionCross);
    setTensionUnit(r.tensionUnit);
    setPrestretch(r.prestretch);
    setPrestretchPercent(r.prestretchPercent);
    setPrestretchCross(r.prestretchCross);
    setPrestretchPercentCross(r.prestretchPercentCross);
    setSelectedGripSku(r.selectedGripSku);
  };

  const resetEditor = () =>
    loadEditorRacket({
      racketName: "",
      isHybrid: false,
      selectedStringId: "",
      selectedStringIdCross: "",
      tensionOption: "standard",
      tensionOptionMain: "standard",
      tensionOptionCross: "standard",
      tensionMain: "",
      tensionCross: "",
      tensionUnit, // keep the customer's unit for the next racket
      prestretch: false,
      prestretchPercent: "10",
      prestretchCross: false,
      prestretchPercentCross: "10",
      selectedGripSku: "",
    });

  const isEditorBlank = () =>
    !racketName.trim() && !selectedStringId && !selectedStringIdCross && !selectedGripSku;

  // Rackets that will be booked: the finished ones plus the racket being filled in,
  // unless that form is closed or was left empty after adding at least one racket
  const getOrderRackets = () =>
    !isEditorOpen || (savedRackets.length > 0 && isEditorBlank())
      ? savedRackets
      : [...savedRackets, getEditorRacket()];

  // Price of one racket, before and after the active discount (applies to every racket)
  const getRacketPrices = (r) => {
    if (serviceKind === "grip") {
      const grip = findGrip(r.selectedGripSku);
      if (!grip) return { original: 0, final: 0 };
      const cost = parseFloat(grip.cost || grip.price || 0) + (isExpress ? 30 : 20);
      return { original: cost, final: cost };
    }

    const main = findString(r.selectedStringId);
    if (!main) return { original: 0, final: 0 };

    if (r.isHybrid) {
      const bothByos = isByosString(main) && isByosString(findString(r.selectedStringIdCross));
      if (bothByos) {
        const original = isExpress ? 100 : 75;
        const final = activeDiscount
          ? (isExpress ? Number(activeDiscount.express_price) : Number(activeDiscount.regular_price))
          : original;
        return { original, final };
      }
      const original = isExpress ? 225 : 200;
      if (activeDiscount) {
        const regDiscount = 75 - Number(activeDiscount.regular_price);
        const expDiscount = 100 - Number(activeDiscount.express_price);
        return { original, final: isExpress ? Math.max(225 - expDiscount, 0) : Math.max(200 - regDiscount, 0) };
      }
      return { original, final: original };
    }

    const original = isExpress ? main.basePrice + 25 : main.basePrice;
    if (activeDiscount) {
      return {
        original,
        final: isExpress
          ? Math.max(main.basePrice - 75 + Number(activeDiscount.express_price), 0)
          : Math.max(main.basePrice - 75 + Number(activeDiscount.regular_price), 0),
      };
    }
    return { original, final: original };
  };

  const orderRackets = getOrderRackets();
  const getOriginalSubtotal = () => orderRackets.reduce((sum, r) => sum + getRacketPrices(r).original, 0);
  const getSubtotal = () => orderRackets.reduce((sum, r) => sum + getRacketPrices(r).final, 0);

  const totalCost = getSubtotal() + pickupFee;
  const discountSavings = getOriginalSubtotal() - getSubtotal();

  // Returns a message if the racket's strings aren't in stock at the chosen store
  const getRacketStockIssue = (r) => {
    if (serviceKind !== "string") return "";
    const missing = [r.selectedStringId, r.isHybrid ? r.selectedStringIdCross : null]
      .map(findString)
      .filter((str) => str && str.string_id !== "byos" && getStockAtLocation(str) <= 0)
      .map((str) => str.name);
    if (missing.length === 0) return "";
    const where = pickupOption === "in-store" ? storeLabel : "any store";
    return `${[...new Set(missing)].join(" and ")} ${missing.length > 1 ? "aren't" : "isn't"} in stock at ${where}`;
  };

  // Checks one racket; returns an error message or ""
  const validateRacket = (r, label) => {
    if (!r.racketName.trim()) return `${label}: please enter the racket brand/model.`;
    if (serviceKind === "grip") {
      return r.selectedGripSku ? "" : `${label}: please select a grip.`;
    }
    if (!r.selectedStringId) return `${label}: please select ${r.isHybrid ? "a main string" : "a string"}.`;
    if (r.isHybrid && !r.selectedStringIdCross) return `${label}: please select a cross string.`;
    if (!r.isHybrid && r.tensionOption === "custom") {
      if (!r.tensionMain) return `${label}: please enter the custom main string tension.`;
      if (!r.tensionCross) return `${label}: please enter the custom cross string tension.`;
    }
    if (r.isHybrid && r.tensionOptionMain === "custom" && !r.tensionMain) {
      return `${label}: please enter the custom main string tension.`;
    }
    if (r.isHybrid && r.tensionOptionCross === "custom" && !r.tensionCross) {
      return `${label}: please enter the custom cross string tension.`;
    }
    const stockIssue = getRacketStockIssue(r);
    if (stockIssue) return `${label}: ${stockIssue}. Please choose another string.`;
    return "";
  };

  const showError = (message) => {
    setErrorMessage(message);
    setTimeout(() => errorRef.current?.scrollIntoView({ behavior: "smooth", block: "center" }), 0);
  };

  // Checks the whole order; returns an error message or ""
  const validateOrder = () => {
    if (!customerName.trim()) return "Please enter your name.";
    if (!customerEmail.trim()) return "Please enter your email.";
    if (!bookingPhone) return "Please enter your phone number.";
    if (pickupOption !== "in-store" && !deliveryAddress.trim()) {
      return "Please enter your address for pickup/delivery.";
    }
    if (serviceSpeed === "Express" && !isExpressAvailable) {
      return expressDisabledReason || "Express service is not available for your current selection.";
    }
    if (orderRackets.length === 0) return "Please add your racket details.";
    if (orderRackets.length > MAX_RACKETS) return `You can book up to ${MAX_RACKETS} rackets at a time.`;
    for (let i = 0; i < orderRackets.length; i++) {
      const error = validateRacket(orderRackets[i], `Racket ${i + 1}`);
      if (error) return error;
    }
    return "";
  };

  const scrollToEditor = () =>
    setTimeout(() => editorRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 0);

  const handleAddAnotherRacket = () => {
    if (!isEditorOpen) {
      setIsEditorOpen(true);
      scrollToEditor();
      return;
    }
    const error = validateRacket(getEditorRacket(), `Racket ${savedRackets.length + 1}`);
    if (error) return showError(error);
    setSavedRackets((prev) => [...prev, getEditorRacket()]);
    resetEditor();
    setStringNotice("");
    setErrorMessage("");
    scrollToEditor();
  };

  // Saves the racket being filled in and closes the form
  const handleSaveRacket = () => {
    const error = validateRacket(getEditorRacket(), `Racket ${savedRackets.length + 1}`);
    if (error) return showError(error);
    setSavedRackets((prev) => [...prev, getEditorRacket()]);
    resetEditor();
    setStringNotice("");
    setErrorMessage("");
    setIsEditorOpen(false);
  };

  // Closes the racket form without saving it (only offered once a racket is saved)
  const handleCancelRacket = () => {
    resetEditor();
    setStringNotice("");
    setIsEditorOpen(false);
  };

  const handleEditRacket = (index) => {
    let list = savedRackets;
    if (isEditorOpen && !isEditorBlank()) {
      const error = validateRacket(getEditorRacket(), `Racket ${savedRackets.length + 1}`);
      if (error) return showError(`Please finish or clear the racket you're filling in first. ${error}`);
      list = [...list, getEditorRacket()];
    }
    setSavedRackets(list.filter((_, i) => i !== index));
    loadEditorRacket(list[index]);
    setIsEditorOpen(true);
    setStringNotice("");
    setErrorMessage("");
    scrollToEditor();
  };

  const handleRemoveRacket = (index) => {
    const remaining = savedRackets.filter((_, i) => i !== index);
    setSavedRackets(remaining);
    // Never leave the customer with no racket form at all
    if (remaining.length === 0) setIsEditorOpen(true);
  };

  const describeTension = (r, which) => {
    const option = which === "main" ? r.tensionOptionMain : which === "cross" ? r.tensionOptionCross : r.tensionOption;
    if (option === "support") return "call me for advice";
    if (option !== "custom") return "standard tension";
    if (which === "main") return `${r.tensionMain} ${r.tensionUnit}`;
    if (which === "cross") return `${r.tensionCross} ${r.tensionUnit}`;
    return `${r.tensionMain}/${r.tensionCross} ${r.tensionUnit}`;
  };

  const describeRacket = (r) => {
    if (serviceKind === "grip") return findGrip(r.selectedGripSku)?.name || "Grip";
    const main = findString(r.selectedStringId)?.name || "String";
    if (!r.isHybrid) return `${main} · ${describeTension(r, "single")}`;
    const cross = findString(r.selectedStringIdCross)?.name || "String";
    return `Hybrid: ${main} (${describeTension(r, "main")}) / ${cross} (${describeTension(r, "cross")})`;
  };

  const handlePayment = async (selectedPaymentOption) => {
    setErrorMessage("");
    const error = validateOrder();
    if (error) return showError(error);
    setPaymentOption(selectedPaymentOption);
    return submitBooking(selectedPaymentOption);
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    return handlePayment("online");
  };

  const submitBooking = async (selectedPaymentOption) => {
    setSubmitting(true);

    try {
      if (!executeRecaptcha) {
        setErrorMessage("reCAPTCHA is not ready. Please refresh or try again.");
        setSubmitting(false);
        return;
      }
      const recaptchaToken = await executeRecaptcha("whatsapp_booking");
      if (!recaptchaToken) {
        setErrorMessage("reCAPTCHA verification failed. Please try again.");
        setSubmitting(false);
        return;
      }

      const bookingDate = new Date().toISOString().split("T")[0];

      const buildBooking = (r) => {
        const main = findString(r.selectedStringId);
        const cross = r.isHybrid ? findString(r.selectedStringIdCross) : null;
        const grip = findGrip(r.selectedGripSku);
        return {
          bigcommerce_customer_id: null, // assigned by backend dynamically
          service_kind: serviceKind,
          racket_name: r.racketName,
          booking_date: bookingDate,
          pickup_option: pickupOption,
          instore_location: pickupOption === "in-store" ? instoreLocation : null,
          delivery_address: pickupOption !== "in-store" ? deliveryAddress : null,
          total_cost: getRacketPrices(r).final, // item cost only

          // String specific fields
          ...(serviceKind === "string" && {
            is_hybrid: r.isHybrid ? 1 : 0,
            string_id: main.string_id === "byos" ? 0 : main.string_id,
            string_name: main.name,
            string_brand: main.brand,
            string_price: r.isHybrid
              ? 0
              : (main.string_id === "byos" ? 0 : main.basePrice - 75),

            string_id_cross: r.isHybrid ? (cross ? (cross.string_id === "byos" ? 0 : cross.string_id) : 0) : null,
            string_name_cross: r.isHybrid && cross ? cross.name : null,
            string_brand_cross: r.isHybrid && cross ? cross.brand : null,
            string_price_cross: r.isHybrid ? 0 : null,

            service_speed: serviceSpeed,
            service_fee: r.isHybrid ? 0 : (activeDiscount
              ? (isExpress ? Number(activeDiscount.express_price) : Number(activeDiscount.regular_price))
              : (isExpress ? 100 : 75)),
            tension_type: !r.isHybrid
              ? r.tensionOption
              : (r.tensionOptionMain === "standard" && r.tensionOptionCross === "standard")
                ? "standard"
                : (r.tensionOptionMain === "support" && r.tensionOptionCross === "support")
                  ? "support"
                  : "custom",
            tension_main: (!r.isHybrid ? r.tensionOption === "custom" : r.tensionOptionMain === "custom") && r.tensionMain ? parseFloat(r.tensionMain) : null,
            tension_cross: (!r.isHybrid ? r.tensionOption === "custom" : r.tensionOptionCross === "custom") && r.tensionCross ? parseFloat(r.tensionCross) : null,
            tension_unit: ((!r.isHybrid && r.tensionOption === "custom") || (r.isHybrid && (r.tensionOptionMain === "custom" || r.tensionOptionCross === "custom"))) ? r.tensionUnit : null,
            prestretch: (!r.isHybrid ? r.tensionOption === "custom" : r.tensionOptionMain === "custom") && r.prestretch ? 1 : 0,
            prestretch_percent: (!r.isHybrid ? r.tensionOption === "custom" : r.tensionOptionMain === "custom") && r.prestretch && r.prestretchPercent ? parseFloat(r.prestretchPercent) : null,
            prestretch_cross: r.isHybrid && r.tensionOptionCross === "custom" && r.prestretchCross ? 1 : 0,
            prestretch_percent_cross: r.isHybrid && r.tensionOptionCross === "custom" && r.prestretchCross && r.prestretchPercentCross ? parseFloat(r.prestretchPercentCross) : null,
          }),

          // Grip specific fields
          ...(serviceKind === "grip" && {
            grip_id: grip.id || grip.variant_id || 1,
            grip_name: grip.name || grip.title,
            string_name: grip.name || grip.title, // sync table compatibility
            service_speed: serviceSpeed,
            service_fee: isExpress ? 30 : 20,
          }),
        };
      };

      const bookingPayload = {
        customer_name: customerName,
        customer_email: customerEmail,
        customer_phone: bookingPhone,
        pickup_fee: pickupFee,
        payment_option: selectedPaymentOption,
        recaptchaToken: recaptchaToken,
        bookings: orderRackets.map(buildBooking),
      };

      const bookingFn = serviceKind === "grip" ? createWhatsappGripBooking : createWhatsappStringBooking;
      const response = await bookingFn(bookingPayload);
      if (response.data?.payment_url) {
        // Redirect to payment page or success page
        window.location.href = response.data.payment_url;
      } else {
        setErrorMessage("Booking creation failed. Please contact support.");
        setSubmitting(false);
      }
    } catch (err) {
      console.error("Booking error:", err);
      showError(err.response?.data?.message || "An error occurred while creating your booking.");
      setSubmitting(false);
    }
  };


  return (
    <div className={styles.wrapper}>
      <div className={styles.card}>
        <div className={styles.header}>
          <div className={styles.logoBadge}>Tennis Restringing</div>
        </div>

        {errorMessage && <div ref={errorRef} className={styles.errorBanner}>{errorMessage}</div>}

        <form onSubmit={handleFormSubmit} className={styles.form}>
          
          {/* Section 1: Customer Contact */}
          <div className={styles.section}>
            <h3>1. Your Details</h3>
            <div className={styles.grid}>
              <div className={styles.field}>
                <label>Full Name</label>
                <input 
                  type="text" 
                  value={customerName} 
                  onChange={(e) => setCustomerName(e.target.value)} 
                  placeholder="e.g. John Doe"
                  required
                />
              </div>
              <div className={styles.field}>
                <label>Email Address</label>
                <input 
                  type="email" 
                  value={customerEmail} 
                  onChange={(e) => setCustomerEmail(e.target.value)} 
                  placeholder="e.g. john@example.com"
                  required
                />
              </div>
              {!linkPhone && (
                <div className={styles.field}>
                  <label>Phone Number (WhatsApp)</label>
                  <input 
                    type="tel" 
                    inputMode="tel"
                    autoComplete="tel"
                    value={customerPhone} 
                    onChange={(e) => setCustomerPhone(e.target.value)} 
                    onBlur={() => setPhoneTouched(true)}
                    placeholder="050 123 4567"
                    required
                  />
                  {phoneTouched && customerPhone.trim() && !typedPhone ? (
                    <span style={{ fontSize: "0.8rem", color: "#c2410c", marginTop: "5px", display: "block" }}>
                      Please double-check this number. If it's a WhatsApp number outside the UAE, start with + and the country code so you receive booking updates.
                    </span>
                  ) : typedPhone ? (
                    <span style={{ fontSize: "0.8rem", color: "#0b7b43", marginTop: "5px", display: "block" }}>
                      ✓ Booking updates will be sent to {typedPhone}
                    </span>
                  ) : (
                    <span style={{ fontSize: "0.8rem", color: "#64748b", marginTop: "5px", display: "block" }}>
                      Mobile, business or landline number. Outside the UAE, start with + and the country code (e.g. +44 7700 900123)
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Section 2: Service Selection */}
          <div className={styles.section}>
            <h3>2. Service Type</h3>
            <div className={styles.buttonGroup}>
              <button 
                type="button"
                className={serviceKind === "string" ? styles.activeBtn : styles.inactiveBtn}
                onClick={() => setServiceKind("string")}
                disabled={savedRackets.length > 0 && serviceKind !== "string"}
              >
                Racket Restringing
              </button>
              <button 
                type="button"
                className={serviceKind === "grip" ? styles.activeBtn : styles.inactiveBtn}
                onClick={() => setServiceKind("grip")}
                disabled={savedRackets.length > 0 && serviceKind !== "grip"}
              >
                Grip Replacement
              </button>
            </div>
            {savedRackets.length > 0 && (
              <span style={{ fontSize: "0.8rem", color: "#64748b", marginTop: "8px", display: "block" }}>
                Restringing and grip replacement are booked separately. Remove your added rackets to switch.
              </span>
            )}
          </div>

          {/* Section 3: Drop-off, store & speed (chosen first so strings match the store's stock) */}
          <div className={styles.section}>
            <h3>3. Logistics & Speed</h3>
            <div className={styles.grid}>
              <div className={styles.field}>
                <label>{serviceKind === "string" ? "Restringing Speed" : "Gripping Speed"}</label>
                <CustomDropdown
                  value={serviceSpeed}
                  onChange={(val) => setServiceSpeed(val)}
                  showSearch={false}
                  options={[
                    {
                      value: "Regular",
                      label: "Regular Speed (within 24-48 hours)",
                      renderLabel: "Regular Speed (within 24-48 hours)",
                      renderOption: "Regular Speed (within 24-48 hours)",
                    },
                    {
                      value: "Express",
                      disabled: !isExpressAvailable,
                      label: isExpressAvailable
                        ? `Express Speed (+ AED ${serviceKind === "string" ? 25 : 10}, within 6 hours)`
                        : `Express Speed (${expressDisabledReason})`,
                      renderLabel: (
                        <span>
                          Express Speed (+ <span className="dirham-symbol">&#xea;</span> {serviceKind === "string" ? 25 : 10}, within 6 hours)
                        </span>
                      ),
                      renderOption: (
                        <div style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
                          <span>
                            Express Speed (+ <span className="dirham-symbol">&#xea;</span> {serviceKind === "string" ? 25 : 10}, within 6 hours)
                          </span>
                          {!isExpressAvailable && (
                            <span style={{ fontSize: "0.78rem", color: "#dc2626", fontWeight: "normal" }}>
                              ⚠️ {expressDisabledReason}
                            </span>
                          )}
                        </div>
                      ),
                    },
                  ]}
                />
                {!isExpressAvailable && (
                  <div className={styles.expressNotice}>
                    <span>⚠️</span>
                    <span>{expressDisabledReason}. Regular Speed is selected.</span>
                  </div>
                )}
              </div>

              <div className={styles.field}>
                <label>Drop-off / Delivery Options</label>
                <CustomDropdown
                  value={pickupOption}
                  onChange={(val) => setPickupOption(val)}
                  showSearch={false}
                  options={[
                    {
                      value: "in-store",
                      label: "In-Store",
                      renderLabel: "In-Store",
                      renderOption: "In-Store",
                    },
                    {
                      value: "pickup",
                      label: `Home Pickup Only - Collect In Store (+ AED ${isExpress ? 50 : 30})`,
                      renderLabel: (
                        <span>
                          Home Pickup Only - Collect In Store (+ <span className="dirham-symbol">&#xea;</span> {isExpress ? 50 : 30})
                        </span>
                      ),
                      renderOption: (
                        <span>
                          Home Pickup Only - Collect In Store (+ <span className="dirham-symbol">&#xea;</span> {isExpress ? 50 : 30})
                        </span>
                      ),
                    },
                    {
                      value: "delivery",
                      label: `Drop-off In Store - Home Delivery (+ AED ${isExpress ? 50 : 30})`,
                      renderLabel: (
                        <span>
                          Drop-off In Store - Home Delivery (+ <span className="dirham-symbol">&#xea;</span> {isExpress ? 50 : 30})
                        </span>
                      ),
                      renderOption: (
                        <span>
                          Drop-off In Store - Home Delivery (+ <span className="dirham-symbol">&#xea;</span> {isExpress ? 50 : 30})
                        </span>
                      ),
                    },
                    {
                      value: "pickup/delivery",
                      label: `Home Pickup & Home Delivery (+ AED ${isExpress ? 100 : 60})`,
                      renderLabel: (
                        <span>
                          Home Pickup & Home Delivery (+ <span className="dirham-symbol">&#xea;</span> {isExpress ? 100 : 60})
                        </span>
                      ),
                      renderOption: (
                        <span>
                          Home Pickup & Home Delivery (+ <span className="dirham-symbol">&#xea;</span> {isExpress ? 100 : 60})
                        </span>
                      ),
                    },
                  ]}
                />
              </div>
            </div>

            {pickupOption === "in-store" && (
              <div className={styles.field} style={{ marginTop: "15px" }}>
                <label>Select Store Location</label>
                <select
                  value={instoreLocation}
                  onChange={(e) => {
                    const newLoc = e.target.value;
                    setInstoreLocation(newLoc);
                    if (newLoc === "Marina" && !expressSettings?.marina_express_enabled && serviceSpeed === "Express") {
                      setServiceSpeed("Regular");
                    }
                    if (newLoc === "Dubai Hills" && !expressSettings?.dubaihills_express_enabled && serviceSpeed === "Express") {
                      setServiceSpeed("Regular");
                    }
                  }}
                >
                  <option value="Jumeirah">Jumeirah Store</option>
                  <option value="Marina">Marina Store</option>
                  <option value="Dubai Hills">Dubai Hills Store</option>
                </select>
                {instoreLocation === "Marina" && !expressSettings?.marina_express_enabled && (
                  <span style={{ fontSize: "0.8rem", color: "#c2410c", marginTop: "5px", display: "block" }}>
                    ℹ️ Note: Express speed is not available at Marina Store (Regular Speed only).
                  </span>
                )}
                {instoreLocation === "Dubai Hills" && !expressSettings?.dubaihills_express_enabled && (
                  <span style={{ fontSize: "0.8rem", color: "#c2410c", marginTop: "5px", display: "block" }}>
                    ℹ️ Note: Express speed is not available at Dubai Hills Store (Regular Speed only).
                  </span>
                )}
              </div>
            )}

            {pickupOption !== "in-store" && (
              <div className={styles.field} style={{ marginTop: "15px" }}>
                <label>Your Address (For Pickup / Delivery)</label>
                <textarea 
                  value={deliveryAddress} 
                  onChange={(e) => setDeliveryAddress(e.target.value)} 
                  placeholder="Street name, Villa/Apartment Number, Area, City"
                  rows="3"
                  required
                />
              </div>
            )}

          </div>

          {/* Section 4: Rackets (up to MAX_RACKETS per booking) */}
          <div className={styles.section}>
            <h3>4. Your Rackets</h3>

            {savedRackets.map((r, index) => {
              const stockIssue = getRacketStockIssue(r);
              return (
                <div key={index} className={styles.racketCard}>
                  <div className={styles.racketCardInfo}>
                    <span className={styles.racketCardTitle}>
                      Racket {index + 1}: {r.racketName}
                    </span>
                    <span className={styles.racketCardDetail}>{describeRacket(r)}</span>
                    {stockIssue && (
                      <span className={styles.racketCardWarning}>⚠️ {stockIssue}. Please edit this racket.</span>
                    )}
                  </div>
                  <div className={styles.racketCardSide}>
                    <span className={styles.racketCardPrice}>
                      <span className="dirham-symbol">&#xea;</span> {getRacketPrices(r).final.toFixed(2)}
                    </span>
                    <div className={styles.racketCardActions}>
                      <button type="button" className={styles.linkBtn} onClick={() => handleEditRacket(index)}>
                        Edit
                      </button>
                      <button type="button" className={styles.linkBtnDanger} onClick={() => handleRemoveRacket(index)}>
                        Remove
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}

            {isEditorOpen && (
            <div ref={editorRef}>
            <div className={styles.editorHeader}>
              <h4 className={styles.editorHeading}>Racket {savedRackets.length + 1}</h4>
              {savedRackets.length > 0 && (
                <button
                  type="button"
                  className={styles.closeRacketBtn}
                  onClick={handleCancelRacket}
                  aria-label={`Close racket ${savedRackets.length + 1}`}
                  title="Close without saving"
                >
                  ×
                </button>
              )}
            </div>
            <div className={styles.field} style={{ marginBottom: "15px" }}>
              <label>Name of racket</label>
              <input 
                type="text" 
                value={racketName} 
                onChange={(e) => setRacketName(e.target.value)} 
                placeholder="e.g. Wilson Pro Staff 97"
              />
            </div>
            
            {loadingInventory ? (
              <p className={styles.loader}>Loading available inventory...</p>
            ) : (
              <>
                {serviceKind === "string" ? (
                  <div className={styles.specContainer}>
                    <div className={styles.field}>
                      <div className={styles.labelWithInfo}>
                        <label>Stringing Setup</label>
                        {isHybrid && (
                          <span
                            ref={hybridInfoRef}
                            className={styles.infoWrap}
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                            }}
                          >
                            <button
                              type="button"
                              className={styles.infoBtn}
                              aria-label="Hybrid pricing information"
                              title="Hybrid pricing information"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                setShowHybridInfo((prev) => !prev);
                              }}
                            >
                              i
                            </button>
                            <div
                              className={`${styles.infoTooltip} ${
                                showHybridInfo ? styles.infoTooltipVisible : ""
                              }`}
                              role="tooltip"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <div style={{ display: "flex", alignItems: "center", gap: "6px", marginBottom: "6px", fontWeight: "700", color: "var(--primary-color, #d2fa10)", fontSize: "0.95rem" }}>
                                <span>ℹ️</span> Consolidated Hybrid Pricing
                              </div>
                              <p style={{ margin: "0 0 6px 0", color: "#f3f4f6" }}>
                                Flat <strong>200 AED</strong> Regular / <strong>225 AED</strong> Express when selecting store strings (or <strong>75 AED</strong> Regular / <strong>100 AED</strong> Express if bringing your own strings for both).
                              </p>
                              <p style={{ margin: 0, fontSize: "0.82rem", color: "#9ca3af" }}>
                                *With Free String Service: 75 AED service fee is deducted &mdash; <strong>125 AED</strong> (200 - 75) Regular / <strong>150 AED</strong> (225 - 75) Express.
                              </p>
                            </div>
                          </span>
                        )}
                      </div>
                      <select 
                        value={isHybrid ? "hybrid" : "single"} 
                        onChange={(e) => {
                          const val = e.target.value === "hybrid";
                          setIsHybrid(val);
                          if (!val) {
                            setSelectedStringIdCross("");
                            setSelectedStringCross(null);
                            setPrestretchCross(false);
                            setPrestretchPercentCross("10");
                          }
                        }}
                      >
                        <option value="single">Single String Setup</option>
                        <option value="hybrid">Hybrid Setup (Main & Cross strings)</option>
                      </select>
                    </div>

                    <div className={styles.field}>
                      <label>{isHybrid ? "Select Main String" : "Select String Model"}</label>
                      <CustomDropdown
                        value={selectedStringId}
                        onChange={(val) => {
                          setSelectedStringId(val);
                          setStringNotice("");
                        }}
                        placeholder="-- Choose a String --"
                        options={sortedStrings.map((str) => {
                          const price = Number(str.basePrice || 0).toFixed(2);
                          return {
                            value: str.string_id,
                            disabled: getStockAtLocation(str) <= 0,
                            searchLabel: `${str.brand} ${str.name}`,
                            renderLabel: (
                              <span>
                                {str.brand} - {str.name}{" "}
                                {!isHybrid && (
                                  <span style={{ color: "#64748b", fontWeight: "normal" }}>
                                    (<span className="dirham-symbol">&#xea;</span> {price})
                                  </span>
                                )}
                              </span>
                            ),
                            renderOption: (
                              <div style={{ display: "flex", flexDirection: "column", gap: "2px", width: "100%" }}>
                                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%" }}>
                                  <span><strong>{str.brand}</strong> - {str.name}</span>
                                  {!isHybrid && (
                                    <span style={{ color: "#0b7b43", fontWeight: "600", fontSize: "0.9rem", whiteSpace: "nowrap", marginLeft: "8px" }}>
                                      <span className="dirham-symbol">&#xea;</span> {price}
                                    </span>
                                  )}
                                </div>
                                {renderStockNote(str)}
                              </div>
                            ),
                          };
                        })}
                      />
                      {stringNotice && (
                        <span style={{ fontSize: "0.8rem", color: "#c2410c", marginTop: "5px", display: "block" }}>
                          ⚠️ {stringNotice}
                        </span>
                      )}
                    </div>

                    {isHybrid && (
                      <div className={styles.field}>
                        <label>Select Cross String</label>
                        <CustomDropdown
                          value={selectedStringIdCross}
                          onChange={(val) => {
                            setSelectedStringIdCross(val);
                            setStringNotice("");
                          }}
                          placeholder="-- Choose a String --"
                          options={sortedStrings.map((str) => {
                            return {
                              value: str.string_id,
                              disabled: getStockAtLocation(str) <= 0,
                              searchLabel: `${str.brand} ${str.name}`,
                              renderLabel: (
                                <span>
                                  {str.brand} - {str.name}
                                </span>
                              ),
                              renderOption: (
                                <div style={{ display: "flex", flexDirection: "column", gap: "2px", width: "100%" }}>
                                  <span><strong>{str.brand}</strong> - {str.name}</span>
                                  {renderStockNote(str)}
                                </div>
                              ),
                            };
                          })}
                        />
                      </div>
                    )}

                    {selectedString && !isHybrid && (
                      <div className={styles.tensionDetails}>
                        <div className={styles.field}>
                          <label>Tension Selection</label>
                          <select 
                            value={tensionOption} 
                            onChange={(e) => setTensionOption(e.target.value)}
                          >
                            <option value="standard">Standard Tension (Recommended)</option>
                            <option value="custom">Custom Specs (Enter details)</option>
                            <option value="support">Call me for advice</option>
                          </select>
                        </div>

                        {tensionOption === "custom" && (
                           <div className={styles.customTensionGrid}>
                             <div className={styles.field}>
                               <label>Main String Tension ({tensionUnit})</label>
                               <input 
                                 type="number" 
                                 value={tensionMain} 
                                 onChange={(e) => setTensionMain(e.target.value)}
                                 placeholder={tensionUnit === "lbs" ? "e.g. 55" : "e.g. 25"}
                                 required
                               />
                             </div>
                             <div className={styles.field}>
                               <label>Cross String Tension ({tensionUnit})</label>
                               <input 
                                 type="number" 
                                 value={tensionCross} 
                                 onChange={(e) => setTensionCross(e.target.value)}
                                 placeholder={tensionUnit === "lbs" ? "e.g. 53" : "e.g. 24"}
                                 required
                               />
                             </div>
                             <div className={styles.field}>
                               <label>Unit</label>
                               <select 
                                 value={tensionUnit} 
                                 onChange={(e) => setTensionUnit(e.target.value)}
                               >
                                 <option value="lbs">lbs</option>
                                 <option value="kgs">kgs</option>
                               </select>
                             </div>
                             
                             <div className={styles.prestretchBlock} style={{ gridColumn: "span 2" }}>
                               <label className={styles.checkboxLabel}>
                                 <input 
                                   type="checkbox" 
                                   checked={prestretch} 
                                   onChange={(e) => setPrestretch(e.target.checked)}
                                 />
                                 Pre-stretch
                               </label>
                               
                               {prestretch && (
                                 <div style={{ display: "inline-flex", alignItems: "center", marginLeft: "10px" }}>
                                   <input 
                                     type="number" 
                                     value={prestretchPercent} 
                                     onChange={(e) => setPrestretchPercent(e.target.value)}
                                     placeholder="10"
                                     style={{ width: "70px" }}
                                     required
                                   />
                                   <span style={{ marginLeft: "5px", color: "var(--text-secondary)" }}>%</span>
                                 </div>
                               )}
                             </div>
                           </div>
                        )}
                      </div>
                    )}

                    {selectedString && isHybrid && (
                      <div className={styles.tensionDetails}>
                        <div style={{ marginBottom: "16px", padding: "12px", background: "#f8f9fa", borderRadius: "8px", border: "1px solid #e2e8f0" }}>
                          <h4 style={{ margin: "0 0 10px 0", color: "#0b7b43", fontSize: "14px", fontWeight: "600" }}>
                            1. Main String Tension ({selectedString?.name || "Main"})
                          </h4>
                          <div className={styles.field}>
                            <select 
                              value={tensionOptionMain} 
                              onChange={(e) => setTensionOptionMain(e.target.value)}
                            >
                              <option value="standard">Standard Tension (Recommended)</option>
                              <option value="custom">Custom Specs (Enter details)</option>
                              <option value="support">Call me for advice</option>
                            </select>
                          </div>

                          {tensionOptionMain === "custom" && (
                            <div className={styles.customTensionGrid} style={{ marginTop: "10px" }}>
                              <div className={styles.field}>
                                <label>Main Tension ({tensionUnit})</label>
                                <input 
                                  type="number" 
                                  value={tensionMain} 
                                  onChange={(e) => setTensionMain(e.target.value)}
                                  placeholder={tensionUnit === "lbs" ? "e.g. 55" : "e.g. 25"}
                                  required
                                />
                              </div>
                              <div className={styles.field}>
                                <label>Unit</label>
                                <select 
                                  value={tensionUnit} 
                                  onChange={(e) => setTensionUnit(e.target.value)}
                                >
                                  <option value="lbs">lbs</option>
                                  <option value="kgs">kgs</option>
                                </select>
                              </div>
                              <div className={styles.prestretchBlock} style={{ gridColumn: "span 2" }}>
                                <label className={styles.checkboxLabel}>
                                  <input 
                                    type="checkbox" 
                                    checked={prestretch} 
                                    onChange={(e) => setPrestretch(e.target.checked)}
                                  />
                                  Main Pre-stretch
                                </label>
                                {prestretch && (
                                  <div style={{ display: "inline-flex", alignItems: "center", marginLeft: "10px" }}>
                                    <input 
                                      type="number" 
                                      value={prestretchPercent} 
                                      onChange={(e) => setPrestretchPercent(e.target.value)}
                                      placeholder="10"
                                      style={{ width: "70px" }}
                                      required
                                    />
                                    <span style={{ marginLeft: "5px", color: "var(--text-secondary)" }}>%</span>
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                        </div>

                        <div style={{ padding: "12px", background: "#f8f9fa", borderRadius: "8px", border: "1px solid #e2e8f0" }}>
                          <h4 style={{ margin: "0 0 10px 0", color: "#0b7b43", fontSize: "14px", fontWeight: "600" }}>
                            2. Cross String Tension ({selectedStringCross?.name || "Cross"})
                          </h4>
                          <div className={styles.field}>
                            <select 
                              value={tensionOptionCross} 
                              onChange={(e) => setTensionOptionCross(e.target.value)}
                            >
                              <option value="standard">Standard Tension (Recommended)</option>
                              <option value="custom">Custom Specs (Enter details)</option>
                              <option value="support">Call me for advice</option>
                            </select>
                          </div>

                          {tensionOptionCross === "custom" && (
                            <div className={styles.customTensionGrid} style={{ marginTop: "10px" }}>
                              <div className={styles.field}>
                                <label>Cross Tension ({tensionUnit})</label>
                                <input 
                                  type="number" 
                                  value={tensionCross} 
                                  onChange={(e) => setTensionCross(e.target.value)}
                                  placeholder={tensionUnit === "lbs" ? "e.g. 53" : "e.g. 24"}
                                  required
                                />
                              </div>
                              <div className={styles.field}>
                                <label>Unit</label>
                                <select 
                                  value={tensionUnit} 
                                  onChange={(e) => setTensionUnit(e.target.value)}
                                >
                                  <option value="lbs">lbs</option>
                                  <option value="kgs">kgs</option>
                                </select>
                              </div>
                              <div className={styles.prestretchBlock} style={{ gridColumn: "span 2" }}>
                                <label className={styles.checkboxLabel}>
                                  <input 
                                    type="checkbox" 
                                    checked={prestretchCross} 
                                    onChange={(e) => setPrestretchCross(e.target.checked)}
                                  />
                                  Cross Pre-stretch
                                </label>
                                {prestretchCross && (
                                  <div style={{ display: "inline-flex", alignItems: "center", marginLeft: "10px" }}>
                                    <input 
                                      type="number" 
                                      value={prestretchPercentCross} 
                                      onChange={(e) => setPrestretchPercentCross(e.target.value)}
                                      placeholder="10"
                                      style={{ width: "70px" }}
                                      required
                                    />
                                    <span style={{ marginLeft: "5px", color: "var(--text-secondary)" }}>%</span>
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className={styles.specContainer}>
                    <div className={styles.field}>
                      <label>Select Grip Replacement</label>
                      <CustomDropdown
                        value={selectedGripSku}
                        onChange={(val) => setSelectedGripSku(val)}
                        placeholder="-- Choose a Grip --"
                        options={grips.map((g) => {
                          const price = Number(g.cost || g.price || 40).toFixed(2);
                          return {
                            value: g.sku,
                            searchLabel: g.name,
                            renderLabel: (
                              <span>
                                {g.name}{" "}
                                <span style={{ color: "#64748b", fontWeight: "normal" }}>
                                  (<span className="dirham-symbol">&#xea;</span> {price})
                                </span>
                              </span>
                            ),
                            renderOption: (
                              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%" }}>
                                <span>{g.name}</span>
                                <span style={{ color: "#0b7b43", fontWeight: "600", fontSize: "0.9rem", whiteSpace: "nowrap", marginLeft: "8px" }}>
                                  <span className="dirham-symbol">&#xea;</span> {price}
                                </span>
                              </div>
                            ),
                          };
                        })}
                      />
                    </div>
                  </div>
                )}
              </>
            )}
            </div>
            )}

            <div className={styles.addRacketRow}>
              {isEditorOpen && (
                <button type="button" className={styles.saveRacketBtn} onClick={handleSaveRacket}>
                  Save racket
                </button>
              )}
              {savedRackets.length + (isEditorOpen ? 1 : 0) < MAX_RACKETS ? (
                <button type="button" className={styles.addRacketBtn} onClick={handleAddAnotherRacket}>
                  + Add another racket
                </button>
              ) : (
                <span className={styles.addRacketLimit}>
                  You can book up to {MAX_RACKETS} rackets at a time.
                </span>
              )}
            </div>
          </div>

          {/* Section 5: Cost summary & pay */}
          <div className={styles.summaryBox}>
            {orderRackets.length > 1 && orderRackets.map((r, index) => (
              <div key={index} className={styles.summaryRow}>
                <span>Racket {index + 1}{r.racketName.trim() ? ` · ${r.racketName.trim()}` : ""}</span>
                <span><span className="dirham-symbol">&#xea;</span> {getRacketPrices(r).original.toFixed(2)}</span>
              </div>
            ))}
            <div className={styles.summaryRow}>
              <span>Service Subtotal{orderRackets.length > 1 ? ` (${orderRackets.length} rackets)` : ""}:</span>
              <span><span className="dirham-symbol">&#xea;</span> {getOriginalSubtotal().toFixed(2)}</span>
            </div>
            {discountSavings > 0 && activeDiscount && (
              <div className={styles.summaryRow} style={{ color: "#16a34a", fontWeight: "600" }}>
                <span>🎁 Applied {activeDiscount.discount_name}:</span>
                <span>- <span className="dirham-symbol">&#xea;</span> {discountSavings.toFixed(2)}</span>
              </div>
            )}
            {pickupOption !== "in-store" && (
              <div className={styles.summaryRow}>
                <span>Home Logistics Fee:</span>
                <span><span className="dirham-symbol">&#xea;</span> {pickupFee.toFixed(2)}</span>
              </div>
            )}
            <div className={`${styles.summaryRow} ${styles.totalRow}`}>
              <span>Total Price (Incl. VAT):</span>
              <span><span className="dirham-symbol">&#xea;</span> {totalCost.toFixed(2)}</span>
            </div>

            {pickupOption === "in-store" ? (
              <div className={styles.paymentButtons}>
                <button 
                  type="button"
                  className={styles.payOnlineBtn} 
                  disabled={submitting}
                  onClick={(e) => {
                    e.preventDefault();
                    handlePayment("online");
                  }}
                >
                  {submitting ? "Processing..." : "Pay Online Now"}
                </button>
                <button 
                  type="button"
                  className={styles.payInStoreBtn} 
                  disabled={submitting}
                  onClick={(e) => {
                    e.preventDefault();
                    handlePayment("in-store");
                  }}
                >
                  {submitting ? "Processing..." : "Pay in Store"}
                </button>
              </div>
            ) : (
              <button 
                type="submit" 
                className={styles.submitBtn} 
                disabled={submitting}
              >
                {submitting ? "Directing to Payment..." : "Confirm & Pay Now"}
              </button>
            )}
          </div>

        </form>
      </div>
    </div>
  );
};

export default WhatsappBookingForm;
